/**
 * v4.33.0 — Multi-organization context switcher.
 *
 * Renders inside the Sidebar above the user badge. When the current
 * user is assigned to MORE THAN ONE organization, this presents:
 *   - All Organizations (aggregated mode, sentinel = `__all__`)
 *   - Each individually assigned organization
 *
 * Persists the selection in `localStorage.yulontap_active_org`. The
 * axios interceptor in `lib/api.js` picks up that value and sends it
 * as the `X-Active-Org-Id` header on every request. The backend
 * `resolve_org_scope` dependency validates membership and either
 * narrows the query to one org or aggregates across the user's set.
 *
 * Backward compatibility:
 *   - Renders `null` for users with ≤ 1 assigned organization. They
 *     experience zero UI change.
 *   - Renders `null` for super admins. They have no scope to switch.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Building2, Layers } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { useAuth } from '../../lib/auth';

const ACTIVE_ORG_STORAGE_KEY = 'yulontap_active_org';
const ALL_ORGS_SENTINEL = '__all__';

export default function OrgSwitcher() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [active, setActive] = useState(() =>
        localStorage.getItem(ACTIVE_ORG_STORAGE_KEY) || ALL_ORGS_SENTINEL
    );

    const orgs = user?.assigned_organizations || [];

    // Hide the switcher for users with no multi-org context.
    // (Super admins are unscoped; single-org users gain nothing from it.)
    const shouldRender = orgs.length > 1;

    // If a user was downgraded from multi-org to single-org while logged
    // in, clear any stale "active org" value so the legacy single-org
    // path takes over again.
    useEffect(() => {
        if (!shouldRender) {
            localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        }
    }, [shouldRender]);

    if (!shouldRender) return null;

    const handleChange = (value) => {
        setActive(value);
        if (value === ALL_ORGS_SENTINEL) {
            // Sentinel is sent explicitly to the backend so it knows the
            // user picked "All Organizations" rather than just forgot.
            localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, ALL_ORGS_SENTINEL);
        } else {
            localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, value);
        }
        // Hard reload so every page-level data fetch picks up the new
        // org scope. Cheaper than threading a global event/observable
        // through the existing React tree.
        window.location.reload();
    };

    return (
        <div className="mb-3 px-2" data-testid="org-switcher">
            <label className="block text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                {t('org_switcher.label')}
            </label>
            <Select value={active} onValueChange={handleChange}>
                <SelectTrigger
                    className="h-9 text-xs w-full"
                    data-testid="org-switcher-trigger"
                >
                    <div className="flex items-center gap-2 min-w-0">
                        {active === ALL_ORGS_SENTINEL ? (
                            <Layers className="w-3.5 h-3.5 text-fuchsia-600 dark:text-fuchsia-400 flex-shrink-0" />
                        ) : (
                            <Building2 className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        )}
                        <SelectValue />
                    </div>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem
                        value={ALL_ORGS_SENTINEL}
                        data-testid="org-switcher-all"
                    >
                        <span className="flex items-center gap-2">
                            <Layers className="w-3.5 h-3.5 text-fuchsia-600" />
                            {t('org_switcher.all_organizations')}
                        </span>
                    </SelectItem>
                    {orgs.map((org) => (
                        <SelectItem
                            key={org.id}
                            value={org.id}
                            data-testid={`org-switcher-option-${org.id}`}
                        >
                            <span className="flex items-center gap-2">
                                <Building2 className="w-3.5 h-3.5 text-blue-600" />
                                {org.name || org.id.slice(0, 8)}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
